import {useState, useMemo} from "react";
import {BatchState} from "@/store/appStore.ts";
import {se} from "react-day-picker/locale";

interface YearMonthSelecterProps {
    availableBatches: BatchState[];
    defaultValue: BatchState | null;
    onChange?: (year: string | null, month: string | null) => void;
    selectStyle?: React.CSSProperties;
}

export const YearMonthSelecter = (
    {
        availableBatches,
        defaultValue,
        onChange,
        selectStyle = {
            fontSize: 12,
            color: '#1a202c',
            fontWeight: 600,
            border: '1px solid #e2e8f0',
            borderRadius: 4,
            padding: '4px 8px',
            background: 'white',
            cursor: 'pointer',
            outline: 'none',
            minWidth: 70,
            fontFamily: 'inherit',
        }
    }: YearMonthSelecterProps) => {

    const [selectedYear, setSelectedYear] = useState<string | null>(defaultValue?.year || null);
    const [selectedMonth, setSelectedMonth] = useState<string | null>(defaultValue?.month || null);

    // 1. 将批次数据转换为按年份分组的结构
    const yearMonths = useMemo(() => {
        const map: Record<string, string[]> = {};
        availableBatches.forEach((batch) => {
            if (!map[batch.year]) {
                map[batch.year] = [];
            }
            // 避免重复添加相同的月份
            if (!map[batch.year].includes(batch.month)) {
                map[batch.year].push(batch.month);
            }
        });
        // 对年份和月份进行排序（假设格式为纯数字或可排序字符串）
        Object.keys(map).forEach((year) => {
            map[year].sort();
        });
        return map;
    }, [availableBatches]);

    const years = useMemo(() => Object.keys(yearMonths).sort(), [yearMonths]);
    const months = useMemo(() => (selectedYear ? yearMonths[selectedYear] || [] : []), [selectedYear, yearMonths]);

    // 处理年份选择
    const handleYearChange = (year: string) => {
        setSelectedYear(year);
        setSelectedMonth(null); // 切换年份时重置月份
        // onChange?.(year, null);
    };

    // 处理月份选择
    const handleMonthChange = (month: string) => {
        setSelectedMonth(month);
        onChange?.(selectedYear, month);
    };

    return (
        <div className="flex flex-col sm:flex-row gap-4 w-full max-w-md">
            {/* 年份选择器 */}
            <div className="flex-1">
                <select
                    value={selectedYear || ""}
                    onChange={(e) => handleYearChange(e.target.value)}
                    style={selectStyle}>
                    <option value="" disabled>
                        year
                    </option>
                    {years.map((year) => (
                        <option key={year} value={year}>
                            {year}
                        </option>
                    ))}
                </select>
            </div>

            {/* 月份选择器 */}
            <div className="flex-1">
                <select
                    value={selectedMonth || ""}
                    onChange={(e) => handleMonthChange(e.target.value)}
                    disabled={!selectedYear}
                    style={selectStyle}>
                    <option value="" disabled>
                        {selectedYear ? "select month" : "select year"}
                    </option>
                    {months.map((month) => (
                        <option key={month} value={month}>
                            {month}
                        </option>
                    ))}
                </select>
            </div>
        </div>
    );
};